import React, { useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info, X } from 'lucide-react';

const TAB_TIPS = {
  overview: "Your portfolio at a glance — total value, profit/loss, and key metrics",
  view_investments: "Browse and manage all your investments in one place",
  allocation: "See how your money is spread across asset types, currencies, and countries",
  performance: "Track how your portfolio value has changed over time",
  entities: "Manage your holding companies, trusts, and other legal structures",
  add_investments: "Add new investments manually or upload them in bulk via CSV",
  share_view: "Share a read-only view of your portfolio with advisors or family",
  faqs: "Answers to common questions about using MyInvestFolio",
  settings: "Update your profile, security, notifications, and preferences",
  sample_investments: "View all sample investments across different asset types",
  sample_allocation: "See how the sample portfolio is distributed across categories",
  sample_performance: "Review returns and top/bottom performers in the sample portfolio",
  portfolio_completeness: "Shows how much detail you've provided for each investment — the more complete, the better your analytics",
  table_view: "See all your investments in a spreadsheet-style table with sortable columns",
  grouped_view: "See your investments organised by ownership type and entity",
  security_settings: "Protect your account with two-factor authentication and login alerts",
  two_factor_auth: "Adds an extra step when you sign in — even if someone gets your password, they can't access your account without your phone",
  manage_active_devices: "See which phones, tablets, and computers are currently signed into your account — and sign out any you don't recognise",
};

/**
 * TabTip — provides tooltip explanations for tabs and section headers.
 * 
 * When `showIcon` is false (default for dashboard tabs), it renders nothing visible
 * and the parent should use the `title` attribute for native hover tooltips.
 * 
 * When `showIcon` is true (for settings sections, sub-tabs, view toggles),
 * it renders a small info icon with hover/tap tooltip.
 *
 * @param {string} [tip] - key into TAB_TIPS for predefined short text
 * @param {React.ReactNode} [content] - custom rich tooltip content (overrides `tip` lookup)
 * @param {boolean} [showIcon=false]
 */
const TabTip = ({ tip: tipKey, content, showIcon = false }) => {
  const text = content !== undefined ? content : TAB_TIPS[tipKey];
  const [open, setOpen] = useState(false);
  if (!text) return null;

  // No icon mode — just return null, parent uses title attribute
  if (!showIcon) return null;

  // Render text as <p> when it's a string, otherwise render the node directly
  const body = typeof text === 'string' ? <p>{text}</p> : text;
  const mobileBody = typeof text === 'string'
    ? <p className="pr-6 leading-relaxed">{text}</p>
    : <div className="pr-6 leading-relaxed">{text}</div>;

  return (
    <>
      {/* Desktop: hover */}
      <span className="hidden md:inline-flex items-center">
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-3.5 h-3.5 text-slate-400 hover:text-blue-500 cursor-help ml-1.5 shrink-0" data-testid="tab-tip-icon-desktop" />
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs text-sm bg-slate-800 text-white p-3 rounded-lg shadow-lg z-9999">
              {body}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </span>

      {/* Mobile: tap */}
      <span className="inline-flex items-center md:hidden">
        <Info
          className="w-3.5 h-3.5 text-slate-400 active:text-blue-500 cursor-pointer ml-1.5 shrink-0"
          data-testid="tab-tip-icon-mobile"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(prev => !prev); }}
          onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(prev => !prev); }}
        />
        {open && (
          <>
            <div className="fixed inset-0 z-9999" onClick={() => setOpen(false)} onTouchEnd={() => setOpen(false)} />
            <div className="fixed left-4 right-4 bottom-24 z-10000 bg-slate-800 text-white text-sm p-4 rounded-xl shadow-2xl border border-slate-700">
              <button
                onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                className="absolute top-2 right-2 text-slate-400 hover:text-white p-1"
                data-testid="tab-tip-close-mobile"
              >
                <X className="w-4 h-4" />
              </button>
              {mobileBody}
            </div>
          </>
        )}
      </span>
    </>
  );
};

export { TAB_TIPS };
export default TabTip;
